<template>
  <div class="report-page">
    <div class="page-header">
      <div>
        <h1 class="page-title">Daily Report</h1>
        <p class="page-subtitle">Task counts by priority and status</p>
      </div>
    </div>

    <!-- Date Picker -->
    <div class="date-row">
      <div class="form-group date-group">
        <label class="form-label">Select Date</label>
        <input v-model="selectedDate" type="date" class="form-control date-input" :max="today" />
      </div>
      <button class="btn btn-primary" @click="fetchReport" :disabled="!selectedDate || loading">
        <span v-if="loading" class="btn-spinner"></span>
        {{ loading ? 'Loading…' : 'Generate Report' }}
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="state-box">
      <div class="spinner"></div>
    </div>

    <!-- Report Data -->
    <div v-else-if="report" class="report-content">
      <div class="report-date-badge">
        📅 Report for {{ formatDate(report.date) }}
      </div>

      <!-- Summary Cards -->
      <div class="summary-grid">
        <div
          v-for="(data, priority) in report.summary"
          :key="priority"
          class="summary-card"
          :class="`priority-${priority}`"
        >
          <div class="summary-header">
            <span class="priority-label">{{ priority }}</span>
            <span class="total-badge">{{ total(data) }} total</span>
          </div>
          <div class="status-bars">
            <div v-for="(count, status) in data" :key="status" class="status-row">
              <div class="status-info">
                <span class="badge" :class="`badge-${status}`">{{ formatStatus(status) }}</span>
                <span class="status-count">{{ count }}</span>
              </div>
              <div class="bar-track">
                <div
                  class="bar-fill"
                  :class="status"
                  :style="{ width: barWidth(count, data) + '%' }"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Grand total -->
      <div class="grand-total card">
        <span class="gt-label">Total tasks on this day</span>
        <span class="gt-number">{{ grandTotal }}</span>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else-if="searched" class="state-box">
      <div class="empty-icon">◫</div>
      <p class="empty-title">No data for this date</p>
      <p style="color: var(--text-muted); font-size: 0.875rem;">No tasks were due on this date.</p>
    </div>

    <!-- Initial state -->
    <div v-else class="state-box">
      <div style="font-size: 2rem; opacity: 0.15;">📊</div>
      <p style="color: var(--text-muted);">Pick a date and click Generate Report</p>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'

export default {
  name: 'ReportView',
  data() {
    return {
      selectedDate: new Date().toISOString().split('T')[0],
      report: null,
      loading: false,
      searched: false
    }
  },
  computed: {
    today() {
      return new Date().toISOString().split('T')[0]
    },
    grandTotal() {
      if (!this.report) return 0
      return Object.values(this.report.summary).reduce((sum, d) => sum + this.total(d), 0)
    }
  },
  methods: {
    async fetchReport() {
      if (!this.selectedDate) return
      this.loading = true
      this.report = null
      this.searched = false
      try {
        const res = await api.getReport(this.selectedDate)
        this.report = res.data
      } catch {
        this.searched = true
      } finally {
        this.loading = false
        this.searched = true
      }
    },
    total(data) {
      return Object.values(data).reduce((a, b) => a + b, 0)
    },
    barWidth(count, data) {
      const t = this.total(data)
      return t === 0 ? 0 : Math.round((count / t) * 100)
    },
    formatStatus(s) {
      return { pending: 'Pending', in_progress: 'In Progress', done: 'Done' }[s] || s
    },
    formatDate(d) {
      return new Date(d).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
    }
  }
}
</script>

<style scoped>
.report-page { animation: fadeIn 0.3s ease; }

.page-title {
  font-family: 'Syne', sans-serif;
  font-weight: 800;
  font-size: 2rem;
  letter-spacing: -0.03em;
  background: linear-gradient(135deg, var(--text) 0%, var(--text-muted) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.page-subtitle { color: var(--text-muted); font-size: 0.875rem; margin-top: 4px; }
.page-header { margin-bottom: 1.5rem; }

.date-row {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  margin-bottom: 2rem;
}

.date-group { flex: 0 0 220px; }
.date-input { font-family: 'DM Sans', sans-serif; }

.report-date-badge {
  display: inline-block;
  padding: 8px 16px;
  background: rgba(79,142,247,0.1);
  border: 1px solid rgba(79,142,247,0.3);
  border-radius: 999px;
  color: var(--accent);
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 1.5rem;
}

.summary-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 16px; margin-bottom: 1rem; }

.summary-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 1.25rem;
  border-top: 3px solid var(--border);
  transition: transform 0.2s;
  animation: slideUp 0.3s ease;
}

.summary-card:hover { transform: translateY(-2px); }
.summary-card.priority-high { border-top-color: var(--high); }
.summary-card.priority-medium { border-top-color: var(--medium); }
.summary-card.priority-low { border-top-color: var(--low); }

.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.priority-label {
  font-family: 'Syne', sans-serif;
  font-weight: 700;
  font-size: 1rem;
  text-transform: capitalize;
}

.priority-high .priority-label { color: var(--high); }
.priority-medium .priority-label { color: var(--medium); }
.priority-low .priority-label { color: var(--low); }

.total-badge {
  background: var(--surface2);
  border: 1px solid var(--border);
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 0.78rem;
  color: var(--text-muted);
}

.status-bars { display: flex; flex-direction: column; gap: 10px; }

.status-row { display: flex; flex-direction: column; gap: 5px; }
.status-info { display: flex; justify-content: space-between; align-items: center; }
.status-count { font-size: 0.9rem; font-weight: 600; color: var(--text); }

.bar-track { height: 5px; background: var(--surface2); border-radius: 999px; overflow: hidden; }
.bar-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.5s ease;
}
.bar-fill.pending { background: var(--text-muted); }
.bar-fill.in_progress { background: var(--accent2); }
.bar-fill.done { background: var(--success); }

.grand-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
}

.gt-label { color: var(--text-muted); font-size: 0.9rem; }
.gt-number { font-family: 'Syne', sans-serif; font-weight: 800; font-size: 2rem; color: var(--accent); }

.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 4rem 2rem;
  text-align: center;
}

.spinner {
  width: 32px; height: 32px;
  border: 2px solid var(--border);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.empty-icon { font-size: 3rem; opacity: 0.2; }
.empty-title { font-family: 'Syne', sans-serif; font-weight: 700; font-size: 1.1rem; color: var(--text); }

.btn-spinner {
  width: 14px; height: 14px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: white;
  border-radius: 50%;
  display: inline-block;
  animation: spin 0.7s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg) } }
@keyframes fadeIn { from { opacity: 0; transform: translateY(8px) } to { opacity: 1; transform: translateY(0) } }
@keyframes slideUp { from { opacity: 0; transform: translateY(12px) } to { opacity: 1; transform: translateY(0) } }
</style>